import { z } from 'zod';

import { defineQuery } from '@nesso/common-contracts/define';
import { practiceIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { subscriptionProductSchema, subscriptionStatusSchema } from './subscription.models';

export const GET_SUBSCRIPTION_ME_TYPE = 'subscription/get-me';
export const LIST_PAID_PLANS_TYPE = 'subscription/list-paid-plans';
export const GET_SUBSCRIPTION_USAGE_HISTORY_TYPE = 'subscription/get-usage-history';
export const SUBSCRIPTION_GET_PRODUCTS_TYPE = 'subscription/get-products';

export const subscriptionSchema = z.object({
    id: z.uuid(),
    practiceId: z.uuid(),
    status: subscriptionStatusSchema,
    planCode: z.string(),
    nextPlanCode: z.string(),
    currentPeriodStart: z.iso.datetime().nullable(),
    currentPeriodEnd: z.iso.datetime().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    counters: z.record(z.string(), z.number()),
    isTrial: z.boolean(),
    trialDaysRemaining: z.number().int().positive().nullable(),
    retentionEndsAt: z.iso.datetime().nullable(),
    periodPriceCents: z.number().int().nonnegative().nullable(),
    limits: z.record(
        z.string(),
        z.object({
            limit: z.number().int().nonnegative(),
            allowOverage: z.boolean(),
            overagePriceCents: z.number().int().nonnegative(),
        }),
    ),
    cardBrand: z.string().nullable(),
    cardLast4: z.string().nullable(),
    cardExpMonth: z.number().int().min(1).max(12).nullable(),
    cardExpYear: z.number().int().min(1000).max(9999).nullable(),
    // The whole next charge — metered overage and any account credit are already applied.
    nextBillTotalCents: z.number().int().nonnegative().nullable(),
    nextInvoiceAt: z.iso.datetime().nullable(),
    nextRetryAt: z.iso.datetime().nullable(),
    canceledAt: z.iso.datetime().nullable(),
    externalCheckoutSessionId: z.string().nullable(),
});

export type Subscription = z.input<typeof subscriptionSchema>;

export const getSubscriptionMe = defineQuery(
    GET_SUBSCRIPTION_ME_TYPE,
    z.object({
        practiceId: practiceIdSchema,
    }),
    subscriptionSchema,
);

export const PRODUCT_STATUSES = ['ACTIVE', 'LAPSED'] as const;

export const productStatusSchema = z.enum(PRODUCT_STATUSES);

export type ProductStatus = z.infer<typeof productStatusSchema>;

export const subscriptionGetProducts = defineQuery(
    SUBSCRIPTION_GET_PRODUCTS_TYPE,
    z.object({
        practiceId: practiceIdSchema,
    }),
    z.array(
        z.object({
            product: subscriptionProductSchema,
            status: productStatusSchema,
        }),
    ),
);

export const paidPlanSchema = z.object({
    code: z.string().meta(metadata.nonSensitive),
    name: z.string().meta(metadata.nonSensitive),
    includedSessions: z.number().int().nonnegative().nullable().meta(metadata.nonSensitive),
    basePriceCents: z.number().int().nonnegative().meta(metadata.nonSensitive),
    overagePriceCents: z.number().int().nonnegative().nullable().meta(metadata.nonSensitive),
    mostPopular: z.boolean().meta(metadata.nonSensitive),
});

export type PaidPlan = z.input<typeof paidPlanSchema>;

export const listPaidPlans = defineQuery(
    LIST_PAID_PLANS_TYPE,
    z.object({}),
    z.array(paidPlanSchema),
);

export const usageHistorySchema = z.object({
    /** Newest cycle first. Cycles with no recorded usage are absent, not zero-filled. */
    periods: z.array(
        z.object({
            periodId: z.string(),
            periodStart: z.iso.datetime().nullable(),
            periodEnd: z.iso.datetime().nullable(),
            planCode: z.string(),
            planName: z.string(),
            counters: z.record(z.string(), z.number()),
            limits: z.record(z.string(), z.number()),
        }),
    ),
});

export type UsageHistory = z.input<typeof usageHistorySchema>;

export const USAGE_HISTORY_DEFAULT_CYCLES = 12;

export const getSubscriptionUsageHistory = defineQuery(
    GET_SUBSCRIPTION_USAGE_HISTORY_TYPE,
    z.object({
        practiceId: practiceIdSchema,
        /** How many billing cycles of history to return, counting the in-flight one. */
        cycles: z.coerce
            .number()
            .pipe(z.union([z.literal(3), z.literal(6), z.literal(12)]))
            .optional(),
    }),
    usageHistorySchema,
);
